import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {IParticularColor} from '@toolify/server/src/services/ColorService/types/IParticularColor'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {ParticularColorType} from '@toolify/server/src/services/ColorService/enum/ParticularColorType'
import {PropertyValueType} from '@toolify/server/src/services/DatabasePropertyService/types/PropertyValueType'
import {converter, formatHex, interpolate, wcagContrast} from 'culori'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {useValueTypeStrategy} from '../../databases/strategies/PropertyValueTypeStrategy/composables/useValueTypeStrategy'
import {useLightModernAppearance} from '@toolify/client/src/modules/theme/composables/useLightModernAppearance'
import {LightModernBadgeVariantType} from '@toolify/server/src/services/UserSettingsService/strategies/LightModernBadgeVariantUserSettingStrategy/enum/LightModernBadgeVariantType'
import {FiordNightThemeVariables} from '@toolify/client/src/modules/theme/strategies/fiord-night/FiordNightThemeVariables'
import {getReadableTextColor, MIN_NON_TEXT_CONTRAST} from '@toolify/client/src/modules/color/functions/getReadableTextColor'
import {ReadableTextBackgroundColorTypes} from '@toolify/client/src/modules/color/constants/ReadableTextBackgroundColorTypes'
import {ReadableIconBackgroundColorTypes} from '@toolify/client/src/modules/color/constants/ReadableIconBackgroundColorTypes'
import {
  getFontColorByBackgroundColor as getFontColorByBackgroundColorForTheme,
} from '@toolify/client/src/modules/color/functions/getFontColorByBackgroundColor'

interface ICellValue{
  value: unknown;
  valueType: PropertyValueType;
  sourceItemId?: string;
}

interface IBadgeHueBoost {
  hueCenter: number
  hueWidth: number
  lightnessBoost: number
  chromaBoost: number
  lightnessSourceFactor?: number
}

interface IBadgeHueShift {
  hueCenter: number
  hueWidth: number
  shift: number
}

interface IBadgeTone {
  backgroundLightness: number
  backgroundLightnessSourceFactor?: number
  backgroundChromaFactor: number
  backgroundChromaMax: number
  backgroundHueBoosts?: IBadgeHueBoost[]
  backgroundHueShifts?: IBadgeHueShift[]
  keepLowChromaBackground?: boolean
  contentLightness: number
  contentChromaFactor: number
  contentChromaMin: number
  contentChromaMax: number
}

const BadgeToneByVariantMap = new Map<LightModernBadgeVariantType, IBadgeTone>([
  [LightModernBadgeVariantType.Pastel, {
    backgroundLightness: 0.93,
    backgroundChromaFactor: 0.55,
    backgroundChromaMax: 0.055,
    contentLightness: 0.36,
    contentChromaFactor: 1.2,
    contentChromaMin: 0.02,
    contentChromaMax: 0.11,
  }],
  [LightModernBadgeVariantType.AccentText, {
    backgroundLightness: 0.9,
    backgroundChromaFactor: 0.7,
    backgroundChromaMax: 0.08,
    contentLightness: 0.45,
    contentChromaFactor: 1.4,
    contentChromaMin: 0.06,
    contentChromaMax: 0.17,
  }],
  [LightModernBadgeVariantType.JuicyWarm, {
    backgroundLightness: 0.64,
    backgroundLightnessSourceFactor: 0.7,
    backgroundChromaFactor: 1.3,
    backgroundChromaMax: 0.21,
    keepLowChromaBackground: true,
    backgroundHueShifts: [
      {
        hueCenter: 40,
        hueWidth: 16,
        shift: 14,
      },
      {
        hueCenter: 175,
        hueWidth: 30,
        shift: 12,
      },
    ],
    backgroundHueBoosts: [
      {
        hueCenter: 90,
        hueWidth: 80,
        lightnessBoost: 0.025,
        chromaBoost: 0.04,
      },
      {
        hueCenter: 25,
        hueWidth: 20,
        lightnessBoost: -0.085,
        chromaBoost: 0,
      },
      {
        hueCenter: 142,
        hueWidth: 14,
        lightnessBoost: -0.032,
        chromaBoost: 0,
        lightnessSourceFactor: 0.65,
      },
    ],
    contentLightness: 0.99,
    contentChromaFactor: 0,
    contentChromaMin: 0,
    contentChromaMax: 0.005,
  }],
])

const lightBadgeBackgroundCache = new Map<string, string>()
const lightBadgeContentCache = new Map<string, string>()

const FiordBadgePaletteColors = FiordNightThemeVariables[ThemeVariableType.PaletteColors]

function getBadgeTone(variant: LightModernBadgeVariantType): IBadgeTone {
  return BadgeToneByVariantMap.get(variant) ?? BadgeToneByVariantMap.get(LightModernBadgeVariantType.AccentText)
}

function toLightBadgeBackground(color: string, variant: LightModernBadgeVariantType): string {
  const cacheKey = `${variant}:${color}`
  const cached = lightBadgeBackgroundCache.get(cacheKey)
  if (cached) {
    return cached
  }
  const tone = getBadgeTone(variant)
  let result: string
  try {
    const okLchConverter = converter('oklch')
    const colorObject = okLchConverter(color)
    const sourceLightness = colorObject.l
    const sourceChroma = colorObject.c ?? 0
    if (tone.keepLowChromaBackground && sourceChroma < 0.05) {
      lightBadgeBackgroundCache.set(cacheKey, color)
      return color
    }
    colorObject.l = tone.backgroundLightness + ((sourceLightness - 0.5) * (tone.backgroundLightnessSourceFactor ?? 0))
    colorObject.c = Math.min(sourceChroma * tone.backgroundChromaFactor, tone.backgroundChromaMax)
    for (const hueShift of tone.backgroundHueShifts ?? []) {
      const hueDistance = Math.abs((((colorObject.h ?? 0) - hueShift.hueCenter + 540) % 360) - 180)
      if (hueDistance < hueShift.hueWidth) {
        colorObject.h = (colorObject.h ?? 0) + (hueShift.shift * (1 - (hueDistance / hueShift.hueWidth)))
      }
    }
    if ((colorObject.c ?? 0) > 0.02) {
      for (const hueBoost of tone.backgroundHueBoosts ?? []) {
        const hueDistance = Math.abs((((colorObject.h ?? 0) - hueBoost.hueCenter + 540) % 360) - 180)
        if (hueDistance < hueBoost.hueWidth) {
          const boostFactor = 1 - (hueDistance / hueBoost.hueWidth)
          colorObject.l += (hueBoost.lightnessBoost + ((sourceLightness - 0.5) * (hueBoost.lightnessSourceFactor ?? 0))) * boostFactor
          colorObject.c += hueBoost.chromaBoost * boostFactor
        }
      }
    }
    result = formatHex(colorObject)
  } catch (error) {
    result = color
  }
  lightBadgeBackgroundCache.set(cacheKey, result)
  return result
}

// neutralChromaThreshold: zrodlo o chromie ponizej progu to szarosc - napis bez wymuszonego minimum nasycenia
// (inaczej neutralna szarosc dostaje napis w odcieniu ok. 250°, czyli niebieskawy); 0 = zawsze z minimum
function toLightBadgeContent(color: string, variant: LightModernBadgeVariantType, neutralChromaThreshold = 0): string {
  const cacheKey = `${variant}:${neutralChromaThreshold}:${color}`
  const cached = lightBadgeContentCache.get(cacheKey)
  if (cached) {
    return cached
  }
  const tone = getBadgeTone(variant)
  let result: string
  try {
    const okLchConverter = converter('oklch')
    const colorObject = okLchConverter(color)
    const sourceChroma = colorObject.c ?? 0
    const contentChromaMin = sourceChroma < neutralChromaThreshold ? 0 : tone.contentChromaMin
    colorObject.l = tone.contentLightness
    colorObject.c = Math.min(Math.max(sourceChroma * tone.contentChromaFactor, contentChromaMin), tone.contentChromaMax)
    result = formatHex(colorObject)
  } catch (error) {
    result = '#000000'
  }
  lightBadgeContentCache.set(cacheKey, result)
  return result
}

// Plakietka light dla dowolnego koloru (np. Custom z wartosci) i wariantu - niezaleznie od aktywnego motywu.
export function getLightBadgeColorsByColorAndVariant(color: string, variant: LightModernBadgeVariantType, neutralChromaThreshold = 0): {backgroundColor: string, contentColor: string} {
  return {
    backgroundColor: toLightBadgeBackground(color, variant),
    contentColor: toLightBadgeContent(color, variant, neutralChromaThreshold),
  }
}

// Ton serii wykresu w light: zawsze nasycony JuicyWarm, niezaleznie od setu plakietek z ustawien - blade
// warianty (Pastel, AccentText) dawaly na bialej karcie slupki-plamy (PR 6, dashboard-08)
const CHART_SERIES_BADGE_VARIANT = LightModernBadgeVariantType.JuicyWarm

export function useParticularColors() {
  const {getThemeVariable} = useThemeVariables()
  const {badgeVariant} = useLightModernAppearance()

  function shouldUseLightBadgeColors(): boolean {
    return Boolean(getThemeVariable.value(ThemeVariableType.UseLightBadgeColors))
  }

  function getLightBadgeNeutralChromaThreshold(): number {
    return getThemeVariable.value(ThemeVariableType.LightBadgeNeutralChromaThreshold) ?? 0
  }

  function getColorByParticularColor(particularColor: IParticularColor): string {
    if (!particularColor?.colorType) {
      return null
    }
    if (particularColor.colorType === ParticularColorType.Custom) {
      const customParticularColor = particularColor as IParticularColor<ParticularColorType.Custom>
      return customParticularColor.payload
    }
    if (particularColor.colorType === ParticularColorType.Palette && typeof particularColor.payload === 'string') {
      return particularColor.payload
    }
    if (particularColor.colorType === ParticularColorType.Palette) {
      const paletteParticularColor = particularColor as IParticularColor<ParticularColorType.Palette>
      const palette = getThemeVariable.value(ThemeVariableType.PaletteColors)
      return palette[paletteParticularColor.payload % palette.length]
    }
    throw new Error(`Unknown color type ${particularColor.colorType}`)
  }

  function getBadgeColorsByParticularColor(particularColor: IParticularColor): {backgroundColor: string, contentColor: string} {
    const rawColor = getColorByParticularColor(particularColor)
    if (!rawColor) {
      return {
        backgroundColor: null,
        contentColor: null,
      }
    }
    if (shouldUseLightBadgeColors()) {
      return getLightBadgeColorsByColorAndVariant(rawColor, badgeVariant.value, getLightBadgeNeutralChromaThreshold())
    }
    return {
      backgroundColor: rawColor,
      contentColor: getFontColorByBackgroundColor(rawColor),
    }
  }

  // Kolor z gotowej wartosci (fullValueToColor: naglowek grupy kanbanu, znacznik elementu osi czasu,
  // kafel wydarzenia) przepuszczony przez to samo mapowanie plakietek light co statusy w tabeli.
  // null = motyw nie uzywa jasnych plakietek (Fiord Night, Dark) - komponent zostaje przy swoim
  // dzisiejszym tle i kolorze tekstu
  function getLightBadgeColorsByColor(color: string): {backgroundColor: string, contentColor: string} | null {
    if (!color || !shouldUseLightBadgeColors()) {
      return null
    }
    return getLightBadgeColorsByColorAndVariant(color, badgeVariant.value, getLightBadgeNeutralChromaThreshold())
  }

  // Kolor serii wykresu (slupek, wycinek, linia). Fiord Night / Dark: surowy kolor jak dotad (getColorByParticularColor).
  // light-modern: ton plakietki JuicyWarm tego koloru - paleta (jak status w tabeli) i Custom (wybrany hex)
  function getChartColorByParticularColor(particularColor: IParticularColor): string {
    const rawColor = getColorByParticularColor(particularColor)
    if (!rawColor || !shouldUseLightBadgeColors()) {
      return rawColor
    }
    if (particularColor.colorType === ParticularColorType.Palette) {
      return getBadgeColorsByStatusColorIndexAndVariant((particularColor as IParticularColor<ParticularColorType.Palette>).payload, CHART_SERIES_BADGE_VARIANT).backgroundColor
    }
    return getLightBadgeColorsByColorAndVariant(rawColor, CHART_SERIES_BADGE_VARIANT).backgroundColor
  }

  // Kolor etykiety danych rysowanej NA serii (wartosc w slupku, podpis wycinka). null = Fiord Night / Dark -
  // wykres zostaje przy dzisiejszym OnPrimary. light-modern: lepszy z dwoch (kontrast WCAG) - wybor po kontrascie
  // (ciemny odcien na zolciach, limonkach, zieleniach: biel ma tam 2,3-2,9:1, dashboard-m03) albo OnPrimary (biel;
  // na nasyconych niebieskich i rdzawych jasny odcien serii czyta sie gorzej niz czysta biel)
  function getChartLabelColorOnSeriesColor(seriesColor: string): string | null {
    if (!seriesColor || !shouldUseLightBadgeColors()) {
      return null
    }
    const onPrimaryColor = getThemeVariable.value(ThemeVariableType.OnPrimaryColor)
    const pickedColor = getFontColorByBackgroundColorForTheme(seriesColor, true)
    return wcagContrast(seriesColor, pickedColor) > wcagContrast(seriesColor, onPrimaryColor) ? pickedColor : onPrimaryColor
  }

  function getBadgeColorsByStatusColorIndexAndVariant(statusColorIndex: number, variant: LightModernBadgeVariantType): {backgroundColor: string, contentColor: string} {
    // light: paleta motywu, jak status w tabeli (getColorByParticularColor) - Grafit ma wlasne szarosci.
    // Poza light (panel podgladu plakietek w Fiord Night / Dark) zostaje paleta Fiord
    const palette: string[] = shouldUseLightBadgeColors()
      ? getThemeVariable.value(ThemeVariableType.PaletteColors)
      : FiordBadgePaletteColors
    const sourceColor = palette[statusColorIndex % palette.length]
    return getLightBadgeColorsByColorAndVariant(sourceColor, variant, getLightBadgeNeutralChromaThreshold())
  }

  function getReadableTextColorByColor(color: string, backgroundTintRatio = 0): string {
    if(!color) {
      return null
    }
    const backgroundColors = ReadableTextBackgroundColorTypes.map(backgroundColorType => {
      const backgroundColor = getThemeVariable.value(backgroundColorType)
      return backgroundTintRatio ? formatHex(interpolate([backgroundColor, color], 'rgb')(backgroundTintRatio)) : backgroundColor
    })
    return getReadableTextColor(color, backgroundColors)
  }

  function getReadableIconColorByColor(color: string): string {
    if(!color) {
      return null
    }
    return getReadableTextColor(color, ReadableIconBackgroundColorTypes.map(backgroundColorType => getThemeVariable.value(backgroundColorType)), MIN_NON_TEXT_CONTRAST)
  }

  function getColorByCellValue(cellValue:ICellValue, databaseProperty?:IDatabasePropertyResponseInstance) {
    const strategy = useValueTypeStrategy(databaseProperty?.valueType || cellValue?.valueType)
    if(strategy?.fullValueToColor && cellValue?.value) {
      const {color} = strategy.fullValueToColor(cellValue.value, databaseProperty)
      return color
    }
    
    return null
  }

  // Fiord Night / Dark: zawsze jasny odcien tla (jak dotad). light-modern: na naprawde jasnym tle
  // (pastel, jasna szarosc, zolc, neutralny chip) ciemny odcien - inaczej tekst znika (T-946)
  function getFontColorByBackgroundColor(backgroundColor: string): string {
    return getFontColorByBackgroundColorForTheme(backgroundColor, shouldUseLightBadgeColors())
  }

  return {
    getColorByParticularColor,
    getBadgeColorsByParticularColor,
    getLightBadgeColorsByColor,
    getBadgeColorsByStatusColorIndexAndVariant,
    getChartColorByParticularColor,
    getChartLabelColorOnSeriesColor,
    getReadableTextColorByColor,
    getReadableIconColorByColor,
    getColorByCellValue,
    getFontColorByBackgroundColor,
  }
}
